import React, { useMemo, useState } from 'react';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { currentSeason, mapMappings } from '../../Constants';
import { mapPhotoMappings } from '../AnalysisPage/mapPhotos';
import TeamLogo from '../../components/TeamLogo';
import { MapStats } from '../../models/MapStats';
import { Team } from '../../models/Team';
import { pct } from './PreMatch2Shared';
import panel from './PreMatch2.module.css';
import styles from './PreMatch2Maps.module.css';

type ViewMode = 'cards' | 'list';

/** One team's record on one map: this season, and every match they have ever played. */
export interface MapRecord {
    won: number;
    played: number;
    seasonWon: number;
    seasonPlayed: number;
}

interface Props {
    team1: Team;
    team2: Team;
    /** teamId -> this season's pick/ban/play counts, straight from the veto endpoint. */
    vetos: Record<string, MapStats[]>;
    /** teamId -> map -> won/played this season and all-time. */
    records: Record<string, Map<string, MapRecord>>;
}

/** One team's numbers for one map. Everything is this season except the `all*` fields, which
 *  are shown as a muted aside so a map the team has not touched yet still has some context. */
interface Side {
    rate: number | null;
    won: number;
    lost: number;
    allRate: number | null;
    allWon: number;
    allLost: number;
    /** False for a brand-new registration; the panel notice covers it once instead. */
    hasHistory: boolean;
    picked: number;
    banned: number;
    played: number;
    ctPct: number;
}

interface Row {
    name: string;
    label: string;
    photo?: string;
    left: Side;
    right: Side;
}

/** The team page's scale, kept identical so a map reads the same colour on both pages. */
const rateClass = (rate: number | null): string => {
    if (rate === null) return styles.rateNone;
    if (rate >= 65) return styles.rateHigh;
    if (rate >= 50) return styles.rateGood;
    if (rate > 0) return styles.rateLow;
    return styles.rateZero;
};

const rateColor = (rate: number | null): string => {
    if (rate === null) return 'rgba(255,255,255,0.15)';
    if (rate >= 65) return '#4ade80';
    if (rate >= 50) return '#e2e8f0';
    if (rate > 0) return '#fbbf24';
    return '#f87171';
};

const buildSide = (
    stats: MapStats | undefined,
    record: MapRecord | undefined,
    hasHistory: boolean,
): Side => ({
    rate: record?.seasonPlayed ? pct(record.seasonWon, record.seasonPlayed) : null,
    won: record?.seasonWon ?? 0,
    lost: record ? record.seasonPlayed - record.seasonWon : 0,
    allRate: record?.played ? pct(record.won, record.played) : null,
    allWon: record?.won ?? 0,
    allLost: record ? record.played - record.won : 0,
    hasHistory,
    picked: stats?.amountPicked ?? 0,
    banned: stats?.amountBanned ?? 0,
    played: stats?.amountPlayed ?? 0,
    ctPct: stats?.chosenCTPercentage ?? 0,
});

const Rate: React.FC<{ side: Side; className: string }> = ({ side, className }) => (
    <span className={`${styles.winRateValue} ${className} ${rateClass(side.rate)}`}>
        {side.rate === null ? '—' : `${side.rate}%`}
    </span>
);

/** The all-time record, deliberately quiet: it is context for the season number, not a rival. */
const AllTime: React.FC<{ side: Side; className: string }> = ({ side, className }) => {
    if (!side.hasHistory) return null;
    return (
        <span className={className} title="Alle sæsoner">
            {side.allRate === null
                ? 'alle: aldrig spillet'
                : `alle: ${side.allRate}% · ${side.allWon}-${side.allLost}`}
        </span>
    );
};

const PickBan: React.FC<{ side: Side }> = ({ side }) => (
    <>
        <span className={`${styles.badge} ${side.picked ? styles.pickBadge : styles.badgeZero}`}>
            {side.picked} pick
        </span>
        <span className={`${styles.badge} ${side.banned ? styles.banBadge : styles.badgeZero}`}>
            {side.banned} ban
        </span>
    </>
);

const SideChoice: React.FC<{ side: Side }> = ({ side }) => {
    if (side.ctPct <= 0) return <span className={styles.dash}>—</span>;
    return (
        <>
            <span className={`${styles.badge} ${styles.ctBadge}`}>CT {side.ctPct}%</span>
            <span className={`${styles.badge} ${styles.tBadge}`}>T {Math.round(100 - side.ctPct)}%</span>
        </>
    );
};

/** A card in the team page's Vetos grid, split down the middle for the two teams. */
const MapCard: React.FC<{ row: Row; team1: Team; team2: Team }> = ({ row, team1, team2 }) => {
    const side = (s: Side, team: Team, right: boolean) => (
        <div className={`${styles.cardSide} ${right ? styles.cardSideRight : ''}`}>
            {/* No crest here: each half is only ~130px wide and the crest was truncating the
                name it is meant to identify. The list view and the page header both carry it. */}
            <span className={styles.cardTeam}>
                <span className={styles.cardTeamName}>{team.teamName}</span>
            </span>
            <Rate side={s} className={styles.cardRate} />
            <span className={styles.cardRecord}>
                {s.rate === null ? (
                    `ingen kampe i S${currentSeason}`
                ) : (
                    <>
                        <span className={styles.wins}>{s.won}W</span>
                        <span className={styles.separator}>-</span>
                        <span className={styles.losses}>{s.lost}L</span>
                    </>
                )}
            </span>
            <AllTime side={s} className={styles.cardAllTime} />
            <span className={`${styles.cardBadges} ${right ? styles.cardBadgesRight : ''}`}>
                <PickBan side={s} />
            </span>
            <span className={`${styles.cardBar} ${right ? styles.cardBarRight : ''}`}>
                <i style={{ width: `${s.rate ?? 0}%`, backgroundColor: rateColor(s.rate) }} />
            </span>
        </div>
    );

    return (
        <div className={styles.card}>
            {row.photo && <img src={row.photo} alt={row.label} className={styles.cardImage} />}
            <div className={styles.cardOverlay} />
            <span className={styles.cardMapName}>{row.label}</span>
            <div className={styles.cardBody}>
                {side(row.left, team1, false)}
                <span className={styles.cardDivider} />
                {side(row.right, team2, true)}
            </div>
        </div>
    );
};

/** A row in the team page's list view, carrying one line per team under a shared map image. */
const MapSection: React.FC<{ row: Row; team1: Team; team2: Team; even: boolean }> = ({
    row, team1, team2, even,
}) => {
    const line = (s: Side, team: Team) => (
        <div className={styles.sideRow} key={team.teamId}>
            <span className={styles.rowTeam}>
                <TeamLogo logo={team.teamLogo} name={team.teamName} className={styles.rowCrest} />
                <span className={styles.rowTeamName}>{team.teamName}</span>
            </span>
            <Rate side={s} className={styles.rowRate} />
            <span className={styles.rowBar}>
                <i style={{ width: `${s.rate ?? 0}%`, backgroundColor: rateColor(s.rate) }} />
            </span>
            <span className={styles.rowRecordCell}>
                <span className={styles.rowRecord}>
                    {s.rate === null ? (
                        <span className={styles.dash}>—</span>
                    ) : (
                        <>
                            <span className={`${styles.rowRecordNum} ${styles.wins}`}>{s.won}</span>
                            <span className={`${styles.rowRecordLabel} ${styles.wins}`}>W</span>
                            <span className={styles.separator}>/</span>
                            <span className={`${styles.rowRecordNum} ${styles.losses}`}>{s.lost}</span>
                            <span className={`${styles.rowRecordLabel} ${styles.losses}`}>L</span>
                        </>
                    )}
                </span>
                <AllTime side={s} className={styles.rowAllTime} />
            </span>
            <span className={styles.rowBadges}><PickBan side={s} /></span>
            <span className={styles.rowSide}><SideChoice side={s} /></span>
            <span className={`${styles.rowPlayed} ${s.played === 0 ? styles.rowPlayedNone : ''}`}>
                {s.played}
            </span>
        </div>
    );

    return (
        <div className={`${styles.section} ${even ? styles.sectionEven : ''}`}>
            <div className={styles.mapArea}>
                {row.photo && <img src={row.photo} alt={row.label} className={styles.mapImg} />}
                <div className={styles.mapOverlay}>
                    <span className={styles.mapLabel}>{row.label}</span>
                </div>
            </div>
            <div className={styles.sides}>
                {line(row.left, team1)}
                {line(row.right, team2)}
            </div>
        </div>
    );
};

/**
 * The /prematch maps tab. Same two views as the team page's Vetos tab and the same toggle
 * between them, with every card and row carrying both teams.
 *
 * Every headline number is this season, so the board agrees with the season it names. The
 * all-time record rides along underneath in grey: early in a season most maps are unplayed, and
 * a team's history is still the best hint at what they will pick.
 */
const PreMatch2Maps: React.FC<Props> = ({ team1, team2, vetos, records }) => {
    const [view, setView] = useState<ViewMode>('cards');

    const rows = useMemo<Row[]>(() => {
        const left = vetos[team1.teamId] ?? [];
        const right = vetos[team2.teamId] ?? [];
        const names = Array.from(new Set([...left, ...right].map(v => v.mapName)));
        const leftHistory = (records[team1.teamId]?.size ?? 0) > 0;
        const rightHistory = (records[team2.teamId]?.size ?? 0) > 0;

        return names
            .map<Row>(name => {
                const key = name.toLowerCase();
                return {
                    name,
                    label: mapMappings.get(`de_${key}`) ?? name,
                    photo: mapPhotoMappings[`de_${key}`],
                    left: buildSide(left.find(v => v.mapName === name), records[team1.teamId]?.get(key), leftHistory),
                    right: buildSide(right.find(v => v.mapName === name), records[team2.teamId]?.get(key), rightHistory),
                };
            })
            // Busiest maps first, weighted so a map the teams actually play outranks one they
            // only ban: a straight count put Cache — never played by either side, but banned five
            // times — above Ancient, which they have played 36 times between them. All-time
            // history breaks ties, and the order holds across both views so the toggle never
            // reshuffles the board.
            .sort((a, b) => {
                const weight = (s: Side) => s.played * 3 + s.picked * 2 + s.banned;
                const activity = (r: Row) => weight(r.left) + weight(r.right);
                const history = (r: Row) =>
                    r.left.allWon + r.left.allLost + r.right.allWon + r.right.allLost;
                return (activity(b) - activity(a))
                    || (history(b) - history(a))
                    || a.label.localeCompare(b.label);
            });
    }, [team1.teamId, team2.teamId, vetos, records]);

    // A team with no recorded maps at all is a new registration, not a gap in the data. Saying
    // "aldrig spillet" on every row reads like breakage; say it once, about the team.
    const noHistory = [team1, team2]
        .filter(t => (records[t.teamId]?.size ?? 0) === 0)
        .map(t => t.teamName);

    return (
        <div className={panel.panel} style={{ marginTop: 18 }}>
            <div className={panel.panelHead}>
                <span className={panel.panelTitle}>Veto og winrate</span>
                <span className={styles.toolbar}>
                    <span className={panel.panelSub}>
                        Sæson {currentSeason} · grå tal: alle sæsoner
                    </span>
                    <button
                        type="button"
                        className={styles.viewToggle}
                        onClick={() => setView(v => (v === 'cards' ? 'list' : 'cards'))}
                        title={view === 'cards' ? 'Skift til liste' : 'Skift til kort'}
                        aria-label={view === 'cards' ? 'Skift til liste' : 'Skift til kort'}
                    >
                        {view === 'cards'
                            ? <ViewListIcon style={{ fontSize: 18 }} />
                            : <GridViewIcon style={{ fontSize: 18 }} />}
                    </button>
                </span>
            </div>

            {noHistory.length > 0 && (
                <div className={styles.notice}>
                    Ingen tidligere kampe registreret for <b>{noHistory.join(' og ')}</b>,
                    så winrate mangler på den side.
                </div>
            )}

            {rows.length === 0 ? (
                <div className={styles.empty}>Ingen veto-data for denne kamp endnu.</div>
            ) : view === 'cards' ? (
                <div className={styles.grid}>
                    {rows.map(row => (
                        <MapCard key={row.name} row={row} team1={team1} team2={team2} />
                    ))}
                </div>
            ) : (
                <>
                    <div className={styles.listHead}>
                        <span className={styles.listHeadMap}>Map</span>
                        <span className={styles.sideRow} style={{ flex: 1 }}>
                            <span>Hold</span>
                            <span>Winrate</span>
                            <span />
                            <span>Record</span>
                            <span>Pick / Ban</span>
                            <span>Side</span>
                            <span style={{ textAlign: 'center' }}>Maps</span>
                        </span>
                    </div>
                    <div className={styles.list}>
                        {rows.map((row, i) => (
                            <MapSection
                                key={row.name}
                                row={row}
                                team1={team1}
                                team2={team2}
                                even={i % 2 === 1}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default PreMatch2Maps;
